import { useFormikContext } from 'formik'
import * as React from 'react'
import * as Api from 'src/api'
import { useAdminAccess } from 'src/helpers/hooks'
import { type AuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import Checkbox from 'src/views/components/forms/formik/Checkbox'
import { FormError } from 'src/views/components/forms/formik/FormError'
import { FormSubmit } from 'src/views/components/forms/formik/FormSubmit'
import SelectInput, { type Option } from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import { GRID_THREE, GRID_TWO } from '../../../programs/AdministratorProgramPage/constants'
import { FormSection } from '../../../programs/AdministratorProgramPage/FormSection'
import { SEMESTERS } from './constants'
import { FacsProgramsSelectField } from './FacsProgramsSelectField'
import { GenCodesSection } from './GenCodesSelectField'
import { LecturerSelectField } from './LecturerSelectField'
import { ProgramCodesSelectField } from './ProgramCodesSelectField'
import { StudentSemesterGrid } from './StudentSemesterGrid'
import { type CourseEditFormValues } from './types'

export function CourseEditFormContent({
  headers,
  course,
  faculties,
  degrees,
  courseDefinitions,
  courseTypeOptions,
  creditTypes,
  isMainAdministrator,
  hideSubmit = false,
}: {
  readonly headers: AuthenticatedHeaders
  readonly course: Api.AdministratorCourse | null
  readonly faculties: readonly Api.Faculty[]
  readonly degrees: readonly Api.AdministratorListsType[] | null | undefined
  readonly courseDefinitions: Api.AdministratorCourseDefinition | null | undefined
  readonly courseTypeOptions: readonly Option[]
  readonly creditTypes: readonly Api.AdministratorListsType[] | null | undefined
  readonly isMainAdministrator: boolean
  readonly hideSubmit?: boolean
}): JSX.Element {
  const t = useTranslatable()
  const canManageCourses = useAdminAccess().canManage('courses')
  const formik = useFormikContext<CourseEditFormValues>()

  const facultyOptions = React.useMemo(() => {
    const fromList = faculties.map((f) => ({ label: f.name, value: String(f.id) }))
    const courseFaculty = course?.faculty?.attributes
    const courseFacultyId = courseFaculty?.id != null ? String(courseFaculty.id) : null
    if (courseFaculty != null && courseFacultyId != null && !fromList.some((o) => o.value === courseFacultyId)) {
      return [{ label: t('common:any'), value: '' }, { label: courseFaculty.name, value: courseFacultyId }, ...fromList]
    }
    return [{ label: t('common:any'), value: '' }, ...fromList]
  }, [course?.faculty?.attributes, faculties, t])

  const facultiesMultiOptions = React.useMemo(() => {
    const fromList = faculties.map((f) => ({ label: f.name, value: String(f.id) }))
    const courseFaculty = course?.faculty?.attributes
    const courseFacultyId = courseFaculty?.id != null ? String(courseFaculty.id) : null
    if (courseFaculty != null && courseFacultyId != null && !fromList.some((o) => o.value === courseFacultyId)) {
      return [{ label: courseFaculty.name, value: courseFacultyId }, ...fromList]
    }
    return fromList
  }, [course?.faculty?.attributes, faculties])

  const degreeOptions = React.useMemo(
    () => [
      { label: t('common:any'), value: '' },
      ...(degrees ?? []).map((d) => ({ label: d.name, value: String(d.id) })),
    ],
    [degrees, t]
  )

  const statusOptions = React.useMemo(
    () => [
      { label: t('common:any'), value: '' },
      ...((courseDefinitions?.statuses ?? []) as readonly Api.Definitions[]),
    ],
    [courseDefinitions?.statuses, t]
  )

  const learningSemesterOptions = React.useMemo(
    () => [
      { label: t('common:any'), value: '' },
      ...SEMESTERS.map((sem) => ({ label: String(sem), value: String(sem) })),
    ],
    [t]
  )

  const creditTypeOptions = React.useMemo(
    () => (creditTypes ?? []).map((ct) => ({ label: ct.name, value: String(ct.id) })),
    [creditTypes]
  )

  const localeOptions = React.useMemo(
    () => [
      { label: t(['ქართული', 'Georgian']), value: 'ka' },
      { label: t(['ინგლისური', 'English']), value: 'en' },
    ],
    [t]
  )

  const showInFreeCreditsOptions = React.useMemo(
    () => [
      { label: t(['კი', 'Yes']), value: '1' },
      { label: t(['არა', 'No']), value: '0' },
    ],
    [t]
  )

  const facsProgramInitialOptions = React.useMemo(
    () =>
      (course?.facsPrograms ?? []).flatMap((fp) => fp.programs.map((p) => ({ value: String(p.id), label: p.name }))),
    [course?.facsPrograms]
  )

  const lecturerInitialLabels = React.useMemo(() => {
    const lecturer = course?.primaryLecturer
    if (lecturer == null || course?.primaryLecturerId == null) return undefined
    const id = String(course.primaryLecturerId)
    const name = lecturer.fullName.trim().length > 0 ? lecturer.fullName.trim() : id
    const email = lecturer.email?.trim() ?? ''
    const label = email.length > 0 ? `${email} - ${name}` : name
    return { [id]: label }
  }, [course?.primaryLecturer, course?.primaryLecturerId])

  const programLabelsRef = React.useRef<Record<string, string>>(
    Object.fromEntries(facsProgramInitialOptions.map((opt) => [opt.value, opt.label]))
  )
  const [programLabels, setProgramLabels] = React.useState<Readonly<Record<string, string>>>(
    () => programLabelsRef.current
  )
  const handleProgramOptionsLoaded = React.useCallback((options: readonly { value: string; label: string }[]) => {
    let changed = false
    for (const opt of options) {
      if (programLabelsRef.current[opt.value] !== opt.label) {
        programLabelsRef.current[opt.value] = opt.label
        changed = true
      }
    }
    if (changed) {
      setProgramLabels({ ...programLabelsRef.current })
    }
  }, [])

  const prevFacultiesRef = React.useRef(formik.values.faculties)
  React.useEffect(() => {
    const prev = prevFacultiesRef.current
    const curr = formik.values.faculties
    prevFacultiesRef.current = curr

    const removed = prev.filter((id) => !curr.includes(id))
    if (removed.length === 0) return

    const currentProgramIds = formik.values.facsProgramIds
    if (currentProgramIds.length === 0) return

    const parsedFacultyIds = curr.map((id) => Number.parseInt(id, 10)).filter((n) => Number.isFinite(n))
    if (parsedFacultyIds.length === 0) {
      void formik.setFieldValue('facsProgramIds', [])
      return
    }

    void (async () => {
      try {
        const result = await Api.getAdministratorPrograms({
          headers,
          query: {
            page: 1,
            perPage: 100,
            status: 1,
            ...(formik.values.degree !== '' ? { degree: Number.parseInt(formik.values.degree, 10) } : {}),
            facultyIds: parsedFacultyIds,
          },
        })
        const validIds = new Set(result.data.map((p) => p.id))
        const filtered = currentProgramIds.filter((id) => validIds.has(id))
        if (filtered.length !== currentProgramIds.length) {
          void formik.setFieldValue('facsProgramIds', filtered)
        }
      } catch {
        // keep current selection on error
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.faculties])

  return (
    <fieldset disabled={formik.isSubmitting || !canManageCourses} className="min-w-0 rounded-card bg-card px-6 py-8">
      <FormError />

      <FormSection title={t('common:detailed_information')}>
        <div className={GRID_TWO}>
          <TextInput required name="description" type="text" label={t('portfolio:description')} />
          <TextInput required name="descriptionEn" type="text" label={t('portfolio:description_en')} />
        </div>
      </FormSection>

      <FormSection title={t('program:faculty_program')}>
        <div className={GRID_TWO}>
          <SelectInput required name="facultyId" label={t('faculty:faculty_name')} options={facultyOptions} />
          <TextInput name="code" type="text" label={t('common:code')} />
        </div>
      </FormSection>

      <FormSection title={t('common:additional_information')}>
        <div className={GRID_THREE}>
          <SelectInput required name="degree" label={t('common:step')} options={degreeOptions} />
          <SelectInput required name="status" label={t('common:status')} options={statusOptions} />
          <SelectInput
            required
            name="typeId"
            label={t('common:type')}
            options={courseTypeOptions}
            disabled={course != null}
          />
        </div>
        <div className={`${GRID_TWO} mt-4`}>
          <SelectInput name="locale" label={t(['ენა', 'Language'])} options={localeOptions} />
          <SelectInput
            name="showInFreeCredits"
            label={t(['თავისუფალ კრედიტებში ჩვენება', 'Show in Free Credits'])}
            options={showInFreeCreditsOptions}
          />
        </div>
      </FormSection>

      <FormSection title={t(['ჩაჭრა / გარიცხვა', 'Fails / Expel'])}>
        <div className={GRID_TWO}>
          <Checkbox
            name="includeExpelCount"
            label={t(['ჩაჭრის გარიცხვის მაჩვენებლის გათვალისწინება', 'Should include Expel count'])}
          />
          <TextInput
            name="failsCount"
            type="number"
            label={t(['ჩაჭრის რაოდენობა', 'Fails count:'])}
            disabled={!formik.values.includeExpelCount}
          />
        </div>
      </FormSection>

      <FormSection title={t(['სტუდენტის არჩევა', 'Student selection'])}>
        <div className="mb-4">
          <Checkbox
            name="chooseRegime"
            label={t(['გამოუჩნდეს სტუდენტს არჩევის რეჟიმში', 'Student selection mode should appear'])}
          />
        </div>
        <StudentSemesterGrid />
      </FormSection>

      <FormSection title={t(['ლექტორი და კრედიტი', 'Lecturer and credit'])}>
        <div className="flex flex-col gap-4">
          <LecturerSelectField headers={headers} initialKnownLabels={lecturerInitialLabels} />
          <TextInput required name="credit" type="number" numberArrowKeysFloatStep label={t('course:credit')} />
          <SelectInput
            required
            name="type"
            label={t('common:type')}
            options={creditTypeOptions}
            disabled={course != null && !isMainAdministrator}
          />
        </div>
      </FormSection>

      <FormSection title={t(['კოდები', 'Codes'])}>
        <div className={GRID_THREE}>
          <ProgramCodesSelectField
            name="requiredCodes"
            label={t(['აუცილებელი', 'Required'])}
            headers={headers}
            degree={formik.values.degree}
          />
          <ProgramCodesSelectField
            name="requiredElectiveCodes"
            label={t(['სავალდებულო არჩევითი', 'Required Elective Courses'])}
            headers={headers}
            degree={formik.values.degree}
          />
          <ProgramCodesSelectField
            name="optionalCodes"
            label={t(['არჩევითი', 'Optional'])}
            headers={headers}
            degree={formik.values.degree}
          />
        </div>
        <GenCodesSection />
      </FormSection>

      <FormSection title={t(['დამატებითი პარამეტრები', 'Additional Parameters'])}>
        <div className="mb-4">
          <Checkbox name="general" label={t('course:general_module_subjects')} />
        </div>
        <div className={GRID_TWO}>
          <SelectInput
            name="studySem"
            label={t(['სასწავლო სემესტრი', 'Learning Semester'])}
            options={learningSemesterOptions}
          />
          <TextInput name="moduleName" type="text" label={t(['მოდულის სახელი', 'Module Name'])} />
          <SelectInput
            name="faculties"
            label={t('faculty:faculties')}
            options={facultiesMultiOptions}
            isMulti
            storeValueOnly
            closeMenuOnSelect={false}
            placeholder={t('faculty:choose_faculty')}
          />
        </div>
        {formik.values.general && (
          <div className="mt-4">
            <FacsProgramsSelectField
              headers={headers}
              degree={formik.values.degree}
              facultyIds={formik.values.faculties}
              onOptionsLoaded={handleProgramOptionsLoaded}
              knownLabels={programLabels}
            />
          </div>
        )}
        <div className={`${GRID_THREE} mt-4`}>
          <TextInput
            name="examCreditLimit"
            type="number"
            label={t(['კურსის გამოცდის ლიმიტი', 'Courses Exam Credit Limit'])}
          />
          <TextInput
            name="scoreMinThreshold"
            type="number"
            label={t(['შეფასების ნორმის ქვედა ზღვარი', 'Assessment Norm Lower Threshold'])}
          />
          <TextInput
            name="scoreMaxThreshold"
            type="number"
            label={t(['შეფასების ნორმის ზედა ზღვარი', 'Assessment Norm Upper Threshold'])}
          />
        </div>
      </FormSection>

      {!hideSubmit && canManageCourses ? (
        <FormSubmit
          label={t('common:save')}
          classNames={{
            root: 'w-full flex justify-end border-t border-borderGrey py-4 mt-4',
            button: 'h-[48px]',
          }}
        />
      ) : null}
    </fieldset>
  )
}
